import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { PropertyLookupHttpService } from './property-lookup.http.service';
import { LookupStore } from '../lookup.store';
import { PropertyLookupSummary } from '../../models/property-lookup-summary';
import { PropertyLookupDetail } from '../../models/property-lookup-detail';
import { LogService } from '../../log/log.service';
import { FlashMessagesService } from '../../flash-messages/service/flash.messages.service';
import { PropertySearchRequest } from '../../models/property-search-request';
import { Page } from '../../models/page';
import { PropertyLookupDetailQuery } from '../../models/property-lookup-detail-query';

@Injectable()
export class PropertyLookupStore implements LookupStore<PropertyLookupSummary, PropertyLookupDetail> {

  private mySelectedResultsCached: BehaviorSubject<PropertyLookupDetail> = new BehaviorSubject(null);
  private mySelectedResults: Subject<PropertyLookupDetail> = new Subject();
  private mySearchResults: BehaviorSubject<List<PropertyLookupSummary>> = new BehaviorSubject(List([]));

  private mySearchResultsPage: BehaviorSubject<Page<PropertyLookupSummary>> = new BehaviorSubject(Page.empty<PropertyLookupSummary>());

  constructor(private propertyLookupService: PropertyLookupHttpService,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService) {
    this.mySelectedResults.subscribe((res: PropertyLookupDetail) => {
      this.mySelectedResultsCached.next(res);
    });
  }

  get selected(): Observable<PropertyLookupDetail> {
    return this.mySelectedResults.asObservable().share();
  }

  get selectedCached(): Observable<PropertyLookupDetail> {
    return this.mySelectedResultsCached.asObservable().filter(res => !!res).share();
  }

  get searchResults(): Observable<List<PropertyLookupSummary>> {
    return this.mySearchResults.asObservable().share();
  }

  clearSearchResults(): void {
    this.mySearchResults.next(List([]));
  }

  search(searchText: string): Observable<List<PropertyLookupSummary>> {
    this.logService.debug(`searchText: ${searchText}`);

    return this.propertyLookupService.search(searchText)
      .catch((error: any) => {
        this.flashMessagesService.warn('MESSAGES.ERROR.PROPERTY_LOOKUP_SEARCH', `Error retrieving PropertyLookupSearch: ${error}`);
        return Observable.throw(error);
      })
      .map((res: List<PropertyLookupSummary>) => {
        this.mySearchResults.next(res);
        return res;
      });
  }

  get advancedSearchTotalElements(): Observable<number> {
    return this.mySearchResultsPage.asObservable().map(page => page.totalElements);
  }

  get advancedSearchSize(): Observable<number> {
    return this.mySearchResultsPage.asObservable().map(page => page.size);
  }

  advancedSearch(searchRequest: PropertySearchRequest): Observable<Page<PropertyLookupSummary>> {
    this.logService.debug(`searchRequest: ${searchRequest}`);

    return this.propertyLookupService.advancedSearch(searchRequest)
      .catch((error: any) => {
        this.flashMessagesService.warn('MESSAGES.ERROR.PROPERTY_LOOKUP_SEARCH', `Error retrieving PropertyLookupSearch: ${error}`);
        return Observable.throw(error);
      })
      .map((res: Page<PropertyLookupSummary>) => {
        this.mySearchResultsPage.next(res);
        this.logService.debug(`searchResult: ${res}`);
        return res;
      });
  }

  getDetail(propertyQuery: PropertyLookupDetailQuery): Observable<PropertyLookupDetail>;
  getDetail(chain: string, brand: string, propertyId: string): Observable<PropertyLookupDetail>;
  getDetail(chainOrPropertyQuery: string| PropertyLookupDetailQuery, brand?: string, propertyId?: string):
    Observable<PropertyLookupDetail> {
    let propertyQuery: PropertyLookupDetailQuery;
    if (typeof chainOrPropertyQuery === 'string') {
      propertyQuery = new PropertyLookupDetailQuery({
        chain: chainOrPropertyQuery,
        brand: brand,
        propertyId: propertyId,
      });
    } else {
      propertyQuery = chainOrPropertyQuery;
    }

    this.logService.debug(`summaryRecord: ${JSON.stringify(propertyQuery)}`);

    return this.propertyLookupService.getDetail(propertyQuery)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.PROPERTY_LOOKUP_GET_DETAILS', `Error retrieving PropertyLookupDetail: ${error}`);
        return Observable.throw(error);
      })
      .map((res: PropertyLookupDetail) => {
        this.mySelectedResults.next(res);
        return res;
      });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/shared/lookup/property-lookup/property-lookup.store.ts